import { ApplicationPermissionMapping, isCustomerLabel } from "api/customerManagement";

/** Accent hue, named rather than hex so a product's colour survives a palette change. */
export type ProductColor =
  | "EvaTeal"
  | "productCyan"
  | "productPurple"
  | "productLime"
  | "productAmber"
  | "productCoral";

export interface Product {
  id: string;
  /** Full product name, as shown under the tile. */
  name: string;
  /** One-line description under the name. Editorial; no server field backs it. */
  blurb: string;
  color: ProductColor;
  /**
   * This product's `url` is absolute because it lives on another host, so no
   * `path` the service reports could describe it.
   *
   * Says nothing about how the link opens — every product opens in a new tab.
   * This decides only whether a server-reported `path` may replace `url`.
   */
  absoluteUrl?: boolean;
  /** Where the product lives. Absolute when `absoluteUrl`, an app path otherwise. */
  url: string;
  /**
   * This product's key in customer-management's `application` table, which is
   * what `GET /users/current/applications` reports.
   *
   * Separate from `id` because the two do not agree and only one of them can
   * change safely: `id` is what `currentId` matches on and what the tests name,
   * while the keys are the server's. Analytics is the case an implicit
   * id-equals-key rule would get wrong in silence — the row is called
   * `dashboard`.
   *
   * Omitted where no row can back the product. See {@link reachableProducts} for
   * what that means, which is not "hide it".
   */
  applicationKey?: string;
}

/**
 * Tile colours. Solid fill carries the Novi mark; `soft` and `line` dress the
 * active tile. Hexes rather than EVA tokens because these identify *products*
 * across the whole Novi suite — they must not drift when EVA restyles, and they
 * must read the same in either theme.
 */
export const PRODUCT_ACCENT: Record<
  ProductColor,
  { solid: string; soft: string; line: string }
> = {
  EvaTeal: { solid: "#00c7b1", soft: "rgba(0, 199, 177, 0.1)", line: "#00c7b1" },
  productCyan: { solid: "#309dba", soft: "rgba(48, 157, 186, 0.1)", line: "#309dba" },
  productPurple: { solid: "#9F85D1", soft: "rgba(159, 133, 209, 0.1)", line: "#9F85D1" },
  productLime: { solid: "#93c24c", soft: "rgba(147, 194, 76, 0.1)", line: "#93c24c" },
  productAmber: { solid: "#ffb23f", soft: "rgba(255, 178, 63, 0.1)", line: "#ffb23f" },
  productCoral: { solid: "#e96141", soft: "rgba(233, 97, 65, 0.1)", line: "#e96141" }
};

/** `id` of EVA's own tile, the one the switcher marks as the open application. */
export const EVA_PRODUCT_ID = "eva";

/**
 * The Novi suite, as the switcher presents it.
 *
 * Presentation only. *Which* of these a given user may reach is
 * customer-management's answer, delivered by
 * `GET /users/current/applications`; this catalog supplies the name, blurb and
 * accent that no server field carries, and `reachableProducts` crosses the two.
 *
 * Provenance varies per field, and the difference matters:
 *
 * - **`applicationKey` is customer-management's**, not ours. `analytics` is
 *   backed by the row called `dashboard`.
 * - **`url` is checked against customer-management's `application` table.** Do
 *   not assume `/{key}` for a new product: the legacy WordPress `intelligence`
 *   is subdomain-routed and carries a NULL path by design. Where the service
 *   reports a `path`, that wins over the value here.
 * - **`absoluteUrl` is about where a product lives, not how it opens.** Every
 *   product opens in a new tab, so every tile carries the new-tab mark. This
 *   flag marks the two that sit on hosts of their own, whose URLs no `path`
 *   column can express.
 * - **`name` and `blurb` are editorial.** customer-management calls them
 *   "Insights", "Intelligence App" and "Intelligence M&A".
 */
export const PRODUCTS: Product[] = [
  {
    /*
     * `dashboard` is absent from the application table in the environments
     * checked so far. With no row naming a permission, the rule keeps this tile
     * rather than hiding it — see reachableProducts for why absence of a row is
     * not evidence against a product. If Analytics gains a row, it starts being
     * gated on that row's permission with no change here.
     */
    id: "analytics",
    applicationKey: "dashboard",
    name: "Novi Analytics",
    blurb: "Well-level forecasting",
    color: "productAmber",
    absoluteUrl: true,
    url: "https://novi.novilabs.io/dashboard"
  },
  {
    id: "insights",
    applicationKey: "insights",
    name: "Novi Insights",
    blurb: "Well screening",
    color: "productCyan",
    url: "/insights"
  },
  {
    /*
     * Keyed on `intelligence-app`, NOT `intelligence`, and the distinction is
     * the one an id-equals-key rule gets exactly backwards. customer-management
     * reports `intelligence-app` with path `/intelligence` (this product, served
     * under the apex) and `intelligence` with a NULL path (the legacy
     * subdomain-routed site below). Keying this on `intelligence` would show
     * Basin Intelligence to anyone licensed only for the WordPress reports site,
     * and hide it from everyone who actually has it.
     */
    id: "intelligence",
    applicationKey: "intelligence-app",
    name: "Novi Basin Intelligence",
    blurb: "Upstream inventory",
    color: "productLime",
    url: "/intelligence"
  },
  {
    /*
     * The legacy WordPress Intelligence site: the `intelligence` row, whose NULL
     * path is what says it is routed by subdomain rather than under the apex.
     * Its absolute URL is kept rather than taken from `path`, because no path
     * column can express another host.
     */
    id: "intelligence-reports",
    applicationKey: "intelligence",
    name: "Novi Intelligence Reports",
    blurb: "Novi research portal",
    color: "productCoral",
    absoluteUrl: true,
    url: "https://intelligence.novilabs.io/"
  },
  {
    id: "intelligence-ma",
    applicationKey: "intelligence-ma",
    name: "Novi M&A Intelligence",
    blurb: "Historical M&A transactions",
    color: "productPurple",
    url: "/intelligence-ma"
  },
  {
    /*
     * EVA itself, and the reason it carries no applicationKey is not an
     * oversight: customer-management has no `eva` row, so the applications
     * endpoint can never name it. A switcher that waited for the server to
     * confirm the app you are looking at would have nowhere to put the "you are
     * here" mark. You are already here; that is the evidence.
     */
    id: EVA_PRODUCT_ID,
    name: "Novi EVA",
    blurb: "Well analytics and forecasting",
    color: "EvaTeal",
    url: "/"
  }
];

/**
 * The tiles to draw: this catalog crossed with what this user may actually reach.
 *
 * Takes both halves rather than reading a store, and neither is guessed. The
 * application table says which permission each product needs — a fact EVA must
 * not hardcode, because the names do not follow from the keys (`insights` is
 * gated by ACCESS_INSIGHT_ENGINE). The permissions come from the user. A
 * product is dropped only when the table names a permission its application
 * needs and the user does not hold it.
 *
 * Everything else is kept, and the one-sidedness is deliberate. The table can
 * be missing a row for a product (EVA has none; the WordPress reports site's is
 * spoken for), and hiding on a missing row would turn a catalogue gap into a
 * navigation control that silently vanishes. A kept tile costs a click and that
 * app's own forbidden page. This is *not* the failure path: a failed call hides
 * the switcher outright, because then there is no answer at all rather than an
 * answer with a gap in it.
 *
 * Links are built per customer, at `<customer>.<apex>/<path>` — see
 * {@link productUrl}. `path` from the table wins over the catalog's `url`,
 * because customer-management owns where its applications live and a path that
 * moved should not need an EVA release to follow it. Products flagged
 * `absoluteUrl` keep theirs, which no `path` column can express.
 */
/**
 * Whether `value` is a path on the target host rather than something that can
 * move the link elsewhere. One leading slash and no second one: `//evil.com/x`
 * is protocol-relative, and anything unrooted (`.evil.com/x`, `@evil.com/x`,
 * `javascript:...`) either re-hosts the link or changes its scheme.
 */
const isAppPath = (value: Maybe<string>): value is string =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

export interface ProductHost {
  /**
   * The customer whose subdomain the apps are served from, or null when none is
   * known. Null links to the apex, which Novi answers by routing a single-
   * customer user onward and offering everyone else its own selector — a better
   * outcome than guessing a subdomain that may not be theirs.
   */
  customer: Nullable<string>;
  /**
   * The Novi apex, e.g. `dev-novilabs.io`. Null in local development, where
   * there is no apex to point at and links stay as paths.
   */
  apexHost: Nullable<string>;
}

/**
 * Where a product's tile should link.
 *
 * The apps are served per customer, at `<customer>.<apex>/<path>`, so a bare
 * path is not enough: left relative it would resolve against EVA's own
 * subdomain, which serves none of them. The path comes from the application
 * table where it has one, because customer-management owns where its
 * applications live.
 *
 * Neither half is trusted with the host. The customer must be a DNS label and
 * the path must be rooted, because either one can otherwise choose where the
 * link goes: a customer of `"evil.com/"` makes the host `evil.com`, and a path
 * of `"@evil.com/x"` or `".evil.com/x"` does the same from the other end. A
 * rejected value falls back to something of ours rather than being patched up,
 * since a link we cannot vouch for is worse than one pointing at the apex.
 *
 * The rooted-path rule also covers the case where there is no apex and the path
 * becomes the href as it stands: `"//evil.com/x"` is protocol-relative and
 * `"javascript:..."` is live, and neither is a path.
 *
 * A product flagged `absoluteUrl` keeps its own URL untouched. Those live on
 * hosts of their own, so neither the customer nor the apex applies. EVA is the
 * same case for the same reason: it is served from its own subdomain, not the
 * customer's, and its tile is a button that links nowhere anyway.
 */
const productUrl = (
  product: Product,
  row: Maybe<ApplicationPermissionMapping>,
  host: ProductHost
): string => {
  if (product.absoluteUrl || product.id === EVA_PRODUCT_ID) return product.url;

  // The catalog's own url is ours; the table's path arrives over the network.
  const path = isAppPath(row?.path) ? row.path : product.url;
  if (!host.apexHost) return path;

  const customer = isCustomerLabel(host.customer) ? `${host.customer}.` : "";
  return `https://${customer}${host.apexHost}${path}`;
};

export const reachableProducts = (
  applications: ApplicationPermissionMapping[],
  permissions: string[],
  host: ProductHost,
  products: Product[] = PRODUCTS
): Product[] => {
  const table = new Map(applications.map((a) => [a.application, a]));

  return products.reduce<Product[]>((kept, product) => {
    const row = product.applicationKey ? table.get(product.applicationKey) : undefined;

    // No row to judge by — see above for why that keeps the product.
    if (row && !permissions.includes(row.permission)) return kept;

    const url = productUrl(product, row, host);
    kept.push(url === product.url ? product : { ...product, url });
    return kept;
  }, []);
};
